export type ShaderCompileParams = {
  uniforms: Record<string, { value: unknown }>;
  vertexShader: string;
  fragmentShader: string;
};

/** IQ-style HSV→RGB, `c.x` = hue 0–1, `c.y` = sat, `c.z` = value. */
const HSV2RGB_FN = `
vec3 rgbWheel_hsv2rgb( vec3 c ) {
	vec4 K = vec4( 1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0 );
	vec3 p = abs( fract( c.xxx + K.xyz ) * 6.0 - K.www );
	return c.z * mix( K.xxx, clamp( p - K.xxx, 0.0, 1.0 ), c.y );
}
`;

const TAU = "6.283185307179586";

/** Number of full spectra by angle around the center (lower -> longer color "waves"). */
const RGB_LED_ANGLE_REPEAT = 3;

const LED_BASIC_MARK = "rgbWheelLedBasicV8";

/**
 * Animation-style enum values shared between TS and the LED fragment shader.
 * Mirrors the `AnimationStyle` union in `types.ts`. Kept as a numeric enum so
 * the shader can branch on a single `int` uniform without string compares.
 *
 * Wrap-safety note: `uRgbWheelPhase` is advanced continuously and wrapped mod
 * 2π in `MirrorScene.RgbDriver`. Any expression inside the shader that uses
 * `phase` directly must be **invariant under that wrap**, otherwise the
 * animation tears at the seam. In practice: any integer multiplier `k` on
 * `phase / TAU` is safe (`fract(k * phase / τ)` is continuous across the
 * seam); non-integer multipliers (`0.5`, `1.7`…) introduce a visible jump.
 */
export const ANIM_STYLE_CODE = {
  classic: 0,
  smoothFade: 1,
  rapidChange: 2,
  chasing: 3,
  symmetrical: 4,
  pulsing: 5,
  converging: 6,
} as const;
/** Number of full spectra per unit of UV projection onto the gradient axis.
 *  The linear component forms the main striped structure; lower values make
 *  the bands wider so fewer cycles are visible across the design at once.
 *  At ~0.5 only half a cycle fits across the bbox long side — the rainbow
 *  reads as a single soft sweep with one obvious hue transition rather than
 *  a striped pattern. */
const RGB_CENTER_LINEAR_REPEAT = 0.5;
/** Number of full spectra per unit radius from the UV center.
 *  The radial component is layered over the linear one and adds color variation
 *  regardless of the current rotation direction. Without it, when the gradient
 *  axis aligns with the bbox's short side, the projection is small and the
 *  content ends up nearly single-color. Kept very low (~0.4) so concentric
 *  rings are wider than the bbox itself — only the centre-to-edge falloff is
 *  visible, never a closed ring, hiding the "rotating circles" structure. */
const RGB_CENTER_RADIAL_REPEAT = 0.4;
/** Gradient-axis spin rate. **Must be an integer**: `phase` in MirrorScene
 *  is wrapped mod 2pi, so for `cos(phase * rate)` to stay continuous across
 *  the wrap seam, a full rotation of the rate-scaled phase must end exactly
 *  at the wrap point. Fractional values (0.5, 0.7...) cause a sudden jump in
 *  gradient direction and visible animation tearing.
 *
 *  1 = one pattern rotation per hue cycle (~6s). 2 = twice as fast. */
const RGB_CENTER_SPIN_RATE = 1;
/** Center text/logo: rotating linear gradient + radial rings (idempotent inject marker).
 *  V2 added `uRgbCenterUvBounds` — the UV sub-rect the pattern is normalized
 *  into, so RGB cycles count is per *content* (text bbox / logo plane), not
 *  per full texture canvas (which can include large transparent padding for
 *  centered short text). */
const CENTER_UV_MARK = "rgbWheelCenterSpinRadialV2";

/**
 * RGB LED without MeshStandard: wheel color * diffuse (white * brightness) only,
 * no directional/specular contribution, so there are no "white" top/right faces.
 *
 * Two angle sources for the hue formula, picked at compile time:
 *  - default: `atan2(vRgbWheelWorldPos.y, .x)` — the fragment's actual world XY
 *    angle. Works for bar contour rings and point-light spheres which sit
 *    exactly on the perimeter (no cross-section displacement).
 *  - `USE_RGB_WHEEL_PERIM_XY`: `atan2(vRgbWheelPerimXY.y, .x)` from a per-vertex
 *    `aPerimXY` attribute that carries the underlying polygon-vertex XY without
 *    any cross-section / extrusion offset. Used by the wall-text frustums and
 *    the rail tubes, so the rail's mitered cross-section at corners can't
 *    shift its hue away from the text on the same wall — both objects sample
 *    the wheel at the same perimeter angle and stay locked together.
 */
export function injectRgbWheelLedMeshBasic(
  shader: ShaderCompileParams,
  phaseUniform: { value: number },
  gainUniform: { value: number },
  animStyleUniform: { value: number },
  paletteIndexUniform: { value: number },
  paletteLutUniform: { value: unknown },
  paletteCount: number,
): void {
  if (
    shader.vertexShader.includes(LED_BASIC_MARK) ||
    shader.fragmentShader.includes(LED_BASIC_MARK)
  ) {
    return;
  }

  shader.uniforms.uRgbWheelPhase = phaseUniform;
  shader.uniforms.uRgbLedGain = gainUniform;
  shader.uniforms.uRgbAnimStyle = animStyleUniform;
  shader.uniforms.uRgbPaletteIndex = paletteIndexUniform;
  shader.uniforms.uRgbPaletteLut = paletteLutUniform;

  shader.vertexShader = shader.vertexShader.replace(
    "#include <common>",
    `#include <common>
varying vec3 vRgbWheelWorldPos;
#ifdef USE_RGB_WHEEL_PERIM_XY
attribute vec2 aPerimXY;
varying vec2 vRgbWheelPerimXY;
#endif`,
  );

  shader.vertexShader = shader.vertexShader.replace(
    "#include <fog_vertex>",
    `#include <fog_vertex>
#ifdef USE_RGB_WHEEL_LED_BASIC
	{
		// Three.js' standard \`project_vertex\` chunk applies \`instanceMatrix\`
		// only to a temporary \`mvPosition\` — the local \`transformed\` itself
		// is *not* mutated. Without folding \`instanceMatrix\` in here, every
		// instance of an InstancedMesh would write the same \`vRgbWheelWorldPos\`
		// (the underlying geometry vertex) and \`atan2(y,x)\` would collapse to
		// a single hue across all instances. That is exactly the regression we
		// hit on point-mode LEDs after switching to InstancedMesh — the RGB
		// wheel rendered as a single colour. Apply the instance transform
		// here so each instance gets its real world XY angle.
		vec3 rgbWheel_localPos = transformed;
		#ifdef USE_INSTANCING
			rgbWheel_localPos = ( instanceMatrix * vec4( rgbWheel_localPos, 1.0 ) ).xyz;
		#endif
		vRgbWheelWorldPos = ( modelMatrix * vec4( rgbWheel_localPos, 1.0 ) ).xyz;
		#ifdef USE_RGB_WHEEL_PERIM_XY
			vRgbWheelPerimXY = aPerimXY;
		#endif
	}
#endif`,
  );

  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <common>",
    `#include <common>
uniform float uRgbWheelPhase;
uniform float uRgbLedGain;
uniform int uRgbAnimStyle;
uniform float uRgbPaletteIndex;
uniform sampler2D uRgbPaletteLut;
varying vec3 vRgbWheelWorldPos;
#ifdef USE_RGB_WHEEL_PERIM_XY
varying vec2 vRgbWheelPerimXY;
#endif`,
  );

  /**
   * 7-mode animation switch — all modes read the same `(angle, phase)` pair,
   * differ only in how they fold them into `(hue, perFragGain)`:
   *
   *  - 0 classic     hue=fract(angle*3 + phase); 3 rainbows around perimeter rotate
   *  - 1 smoothFade  hue=fract(phase); entire frame is one slowly-cycling hue
   *  - 2 rapidChange same as 1 but ×5 phase rate (integer keeps wrap clean)
   *  - 3 chasing     fixed slow hue drift + per-fragment comet pulse traveling
   *                  around the perimeter (`pow(1-fract(theta*N - phase*2), 4)`)
   *  - 4 symmetrical hue depends on the *signed distance from the nearest
   *                  side midpoint* (theta folded into quadrant-half) — both
   *                  ends of each edge show the same color, wave moves from
   *                  edge midpoint outward to corners
   *  - 5 pulsing     slow hue drift + global brightness "breathing"
   *                  (`sin²(phase * 2)` — integer-safe across wrap)
   *  - 6 converging  single directional flow once around perimeter:
   *                  hue=fract(theta/τ + phase). No 3× repeat, no atan2
   *                  symmetry, so the band reads as a continuous stream
   *                  flowing toward a single corner.
   */
  shader.fragmentShader = shader.fragmentShader.replace(
    "\tvec3 outgoingLight = reflectedLight.indirectDiffuse;",
    `\tvec3 outgoingLight = reflectedLight.indirectDiffuse;
#ifdef USE_RGB_WHEEL_LED_BASIC
	{
		#ifdef USE_RGB_WHEEL_PERIM_XY
			float ${LED_BASIC_MARK}_a = atan( vRgbWheelPerimXY.y, vRgbWheelPerimXY.x );
		#else
			float ${LED_BASIC_MARK}_a = atan( vRgbWheelWorldPos.y, vRgbWheelWorldPos.x );
		#endif
		float ${LED_BASIC_MARK}_phase01 = uRgbWheelPhase / ${TAU};
		float ${LED_BASIC_MARK}_t01 = fract( ${LED_BASIC_MARK}_a / ${TAU} + 0.5 ); // [0,1) around perimeter
		float hueLed = 0.0;
		float gainLed = 1.0;
		float satLed = 1.0;
		if ( uRgbAnimStyle == 1 ) {
			// smoothFade
			hueLed = fract( ${LED_BASIC_MARK}_phase01 );
		} else if ( uRgbAnimStyle == 2 ) {
			// rapidChange — ×5 keeps integer wrap (clean seam at phase=2π)
			hueLed = fract( ${LED_BASIC_MARK}_phase01 * 5.0 );
		} else if ( uRgbAnimStyle == 3 ) {
			// chasing — slow hue drift + comet pulses sweeping around the perimeter
			hueLed = fract( ${LED_BASIC_MARK}_phase01 );
			float ${LED_BASIC_MARK}_c = fract( ${LED_BASIC_MARK}_t01 * 6.0 - ${LED_BASIC_MARK}_phase01 * 2.0 );
			gainLed = pow( 1.0 - ${LED_BASIC_MARK}_c, 4.0 ) + 0.08;
		} else if ( uRgbAnimStyle == 4 ) {
			// symmetrical — fold theta into [0, τ/4], measure normalized
			// distance from the quadrant midpoint (=side midpoint) to the
			// nearest corner; same value mirrors across all 4 sides
			float ${LED_BASIC_MARK}_q = mod( ${LED_BASIC_MARK}_a + ${TAU}, ${TAU} * 0.25 );
			float ${LED_BASIC_MARK}_mid = ${TAU} * 0.125;
			float ${LED_BASIC_MARK}_dn = abs( ${LED_BASIC_MARK}_q - ${LED_BASIC_MARK}_mid ) / ${LED_BASIC_MARK}_mid; // [0,1]
			hueLed = fract( ${LED_BASIC_MARK}_dn + ${LED_BASIC_MARK}_phase01 );
		} else if ( uRgbAnimStyle == 5 ) {
			// pulsing — slow hue drift, brightness breathing
			hueLed = fract( ${LED_BASIC_MARK}_phase01 );
			float ${LED_BASIC_MARK}_s = sin( uRgbWheelPhase * 2.0 );
			gainLed = 0.25 + 0.75 * ${LED_BASIC_MARK}_s * ${LED_BASIC_MARK}_s;
		} else if ( uRgbAnimStyle == 6 ) {
			// converging — diagonal mirror. Two convergence corners are
			// antipodal in angle space (TL = 3π/4, BR = −π/4), so folding θ
			// by π collapses them onto a single phi=3π/4. The shortest arc
			// distance from any fragment's phi to that point (∈ [0, π/2])
			// is then 0 at TL/BR and π/2 at the departure corners TR/BL.
			// Sign on phase01 = "+" → hue at any d moves toward d=0, i.e.
			// flow streams into the convergence corner from both sides.
			float ${LED_BASIC_MARK}_phi = mod( ${LED_BASIC_MARK}_a + ${TAU}, ${TAU} * 0.5 );
			float ${LED_BASIC_MARK}_raw = abs( ${LED_BASIC_MARK}_phi - ${TAU} * 0.375 );
			float ${LED_BASIC_MARK}_dCv = min( ${LED_BASIC_MARK}_raw, ${TAU} * 0.5 - ${LED_BASIC_MARK}_raw );
			float ${LED_BASIC_MARK}_dn = ${LED_BASIC_MARK}_dCv / ( ${TAU} * 0.25 );
			hueLed = fract( ${LED_BASIC_MARK}_dn + ${LED_BASIC_MARK}_phase01 );
		} else {
			// classic (default, including legacy 0)
			hueLed = fract( ${LED_BASIC_MARK}_a * float(${RGB_LED_ANGLE_REPEAT}) / ${TAU} + ${LED_BASIC_MARK}_phase01 + 0.25 );
		}
		// Sample the active palette row. Y coord centers within the row
		// (idx + 0.5) / N so a LinearFilter on the Y axis never blends two
		// neighbouring palettes — only the X axis (hue) is interpolated.
		vec2 ${LED_BASIC_MARK}_lutUv = vec2( hueLed, ( uRgbPaletteIndex + 0.5 ) * ${(1 / paletteCount).toFixed(8)} );
		vec3 wheelCol = texture2D( uRgbPaletteLut, ${LED_BASIC_MARK}_lutUv ).rgb;
		wheelCol *= satLed;
		// Output is HDR (>1) on purpose: the postprocessing Bloom pass uses a
		// luminance threshold around 1.0, so we *want* the LED material to
		// punch above it. The display clamps the visible center to the
		// dominant channel (saturated hue / soft white at very high gain),
		// while bloom catches the >1 spillover and renders the colored halo.
		outgoingLight = wheelCol * diffuse * uRgbLedGain * gainLed;
	}
#endif`,
  );
}

/**
 * Maximum number of depth-bands the wall-text uniform array is sized for.
 * Three.js' GLSL `uniform float uBandOpacities[N];` declaration needs `N` as
 * a compile-time literal, so we pin it to the largest `state.depthLayers`
 * the UI can produce (well above the typical 11). Increasing it costs one
 * float of uniform space per slot, which is negligible.
 */
const MAX_BAND_LAYERS = 32;

const BAND_ALPHA_MARK = "wallTextBandAlphaV1";

/**
 * Per-vertex alpha multiplier sourced from a uniform array, indexed by a
 * per-vertex `aBandIdx` attribute. Lets `WallTextSection` fold N depth
 * bands into a single merged-band mesh while keeping each band's
 * brightness-faded opacity (`layerOpacityAt(bandIdx, nLayers, brightness)`).
 *
 * Updates to `state.brightness` mutate `bandOpacitiesUniform.value` in
 * place — the shader picks the new values up on the next render with no
 * geometry rebuild.
 *
 * Idempotent (repeat `onBeforeCompile` calls don't re-inject) via the
 * `BAND_ALPHA_MARK` comment in the fragment source.
 */
export function injectBandAlpha(
  shader: ShaderCompileParams,
  bandOpacitiesUniform: { value: Float32Array },
): void {
  if (shader.fragmentShader.includes(BAND_ALPHA_MARK)) return;

  shader.uniforms.uBandOpacities = bandOpacitiesUniform;

  shader.vertexShader = shader.vertexShader.replace(
    "#include <common>",
    `#include <common>
attribute float aBandIdx;
uniform float uBandOpacities[${MAX_BAND_LAYERS}];
varying float vBandAlpha;`,
  );

  shader.vertexShader = shader.vertexShader.replace(
    "#include <begin_vertex>",
    `#include <begin_vertex>
vBandAlpha = uBandOpacities[ int( aBandIdx + 0.5 ) ];`,
  );

  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <common>",
    `#include <common>
varying float vBandAlpha;
// ${BAND_ALPHA_MARK}`,
  );

  // Apply at `<opaque_fragment>` so we run after texture sampling, alphaTest
  // discard, and the LED-gain emission injection (which only writes RGB).
  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <opaque_fragment>",
    `#include <opaque_fragment>
gl_FragColor.a *= vBandAlpha;`,
  );
}

/**
 * Solid LED gain for `MeshBasicMaterial`: multiplies `outgoingLight` by
 * `uLedSolidGain` so the LED color punches above the bloom luminance
 * threshold. Same idea as the RGB path above, but for the
 * single-color (`state.ledColor`) materials used by the bar tubes, point
 * discs, wall-text bands, and rails when RGB is off.
 *
 * The visible LED center clamps to (≤1, ≤1, ≤1) at the framebuffer (so
 * the chosen color stays recognizable at moderate gain, then washes
 * toward white at high gain — what real LEDs look like), and the
 * postprocessing pass catches the HDR overflow as a colored halo.
 *
 * Idempotent via the `LED_SOLID_GAIN_MARK` comment, so repeat
 * `onBeforeCompile` calls (R3F often re-compiles on prop changes) do
 * not stack the multiplier.
 */
const LED_SOLID_GAIN_MARK = "ledSolidGainV1";

export function injectLedSolidGain(
  shader: ShaderCompileParams,
  gainUniform: { value: number },
): void {
  if (shader.fragmentShader.includes(LED_SOLID_GAIN_MARK)) return;

  shader.uniforms.uLedSolidGain = gainUniform;

  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <common>",
    `#include <common>
uniform float uLedSolidGain;
// ${LED_SOLID_GAIN_MARK}`,
  );

  shader.fragmentShader = shader.fragmentShader.replace(
    "\tvec3 outgoingLight = reflectedLight.indirectDiffuse;",
    `\tvec3 outgoingLight = reflectedLight.indirectDiffuse;
#ifdef USE_LED_GAIN_SOLID
	outgoingLight *= uLedSolidGain;
#endif`,
  );
}

/**
 * Text/logo: rotating linear gradient + concentric rings around the *content* center.
 *
 * Two overlaid hue components:
 *  - linear (`t * LINEAR`): parallel bands perpendicular to the gradient direction.
 *    This direction rotates around the bbox center with phase, so the whole rainbow
 *    pattern rotates around the text/logo center as a single unit.
 *  - radial (`r * RADIAL`): concentric rings centered on the bbox, rotationally
 *    symmetric. This guarantees variation even when the rotating axis aligns with
 *    the short side (small projection -> fewer colors from linear).
 *
 * `uRgbCenterUvBounds` (vec4 = (uMin.xy, uMax.xy)) defines the UV sub-rect the
 * pattern is anchored to. The shader translates `vMapUv` so the bbox centre maps
 * to (0,0) and then divides BOTH axes by the **larger** bbox half-extent, so:
 *   - Along the long axis: d ∈ [−0.5, 0.5] (one full unit across the bbox).
 *   - Along the short axis: d span is proportionally smaller (= aspect ratio).
 * This keeps the pattern visually round/square regardless of how flat the text
 * line is — concentric rings stay circular, and linear stripes have the same
 * spatial period whichever way the gradient is rotated. The bbox short axis
 * shows only a narrow slice of the pattern instead of the whole thing being
 * squashed into it. Set bounds to (0, 0, 1, 1) to anchor on the full texture
 * (logos, wall-text).
 *
 * Hue is also shifted cyclically with phase, so the center does not get stuck on
 * one color. No `atan2` is used, so there is no angular singularity.
 */
export function injectRgbWheelCenterShader(
  shader: ShaderCompileParams,
  phaseUniform: { value: number },
  uvBoundsUniform: { value: { x: number; y: number; z: number; w: number } },
): void {
  if (shader.fragmentShader.includes(CENTER_UV_MARK)) return;

  shader.uniforms.uRgbWheelPhase = phaseUniform;
  shader.uniforms.uRgbCenterUvBounds = uvBoundsUniform;

  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <common>",
    `#include <common>
${HSV2RGB_FN}
uniform float uRgbWheelPhase;
uniform vec4 uRgbCenterUvBounds;
// ${CENTER_UV_MARK}`,
  );

  shader.fragmentShader = shader.fragmentShader.replace(
    "#include <map_fragment>",
    `#include <map_fragment>
#ifdef USE_RGB_WHEEL_CENTER
	{
		vec2 ${CENTER_UV_MARK}_bbMin = uRgbCenterUvBounds.xy;
		vec2 ${CENTER_UV_MARK}_bbMax = uRgbCenterUvBounds.zw;
		vec2 ${CENTER_UV_MARK}_bbSize = max( ${CENTER_UV_MARK}_bbMax - ${CENTER_UV_MARK}_bbMin, vec2( 1e-4 ) );
		vec2 ${CENTER_UV_MARK}_bbCenter = 0.5 * ( ${CENTER_UV_MARK}_bbMin + ${CENTER_UV_MARK}_bbMax );
		// Single isotropic divisor = bbox long side, so the short axis keeps its
		// real proportion instead of being remapped to [-0.5, 0.5]. Rings stay
		// round and stripe spacing is direction-independent.
		float ${CENTER_UV_MARK}_bbScale = max( ${CENTER_UV_MARK}_bbSize.x, ${CENTER_UV_MARK}_bbSize.y );
		vec2 ${CENTER_UV_MARK}_d = ( vMapUv - ${CENTER_UV_MARK}_bbCenter ) / ${CENTER_UV_MARK}_bbScale;
		float ${CENTER_UV_MARK}_r = length( ${CENTER_UV_MARK}_d );
		float ${CENTER_UV_MARK}_spin = uRgbWheelPhase * float(${RGB_CENTER_SPIN_RATE});
		vec2 ${CENTER_UV_MARK}_dir = vec2( cos(${CENTER_UV_MARK}_spin), sin(${CENTER_UV_MARK}_spin) );
		float ${CENTER_UV_MARK}_t = dot( ${CENTER_UV_MARK}_d, ${CENTER_UV_MARK}_dir );
		float ${CENTER_UV_MARK}_hue = fract(
			${CENTER_UV_MARK}_t * float(${RGB_CENTER_LINEAR_REPEAT})
			+ ${CENTER_UV_MARK}_r * float(${RGB_CENTER_RADIAL_REPEAT})
			+ uRgbWheelPhase / ${TAU}
			+ 0.5
		);
		vec3 wcol = rgbWheel_hsv2rgb( vec3( ${CENTER_UV_MARK}_hue, 0.94, 0.82 ) );
		diffuseColor.rgb *= wcol;
		{
			vec3 cTxt = diffuseColor.rgb;
			float mTxt = max( cTxt.r, max( cTxt.g, cTxt.b ) );
			diffuseColor.rgb = mTxt > 1.0 ? cTxt / mTxt : cTxt;
		}
	}
#endif`,
  );
}
